import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../Firebase/Firebase";
import { onAuthStateChanged } from "firebase/auth";
import { navigate } from "gatsby";

const defaultContext = {
  currentUser: null,
  fetchAuth: async () => {
    if (typeof window !== "undefined") {
      await navigate("/welcome/");
    }
    return null;
  },
};

export const AuthContext = createContext(defaultContext);

const endPoint = `${process.env.GATSBY_API_URL || "/api/"}`;

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  const isTokenExpired = async () => {
    if (!currentUser) return true;
    
    try {
      const token = await currentUser.getIdTokenResult();
      
      const expirationTime = new Date(token.expirationTime).getTime();
      const nowTime = new Date().getTime();
      
      return (expirationTime - nowTime) < 5 * 60 * 1000;
    } catch (error) {
      return true;
    }
  };

  const refreshTokenIfNeeded = async () => {
    if (!currentUser) return null;
    
    try {
      const expired = await isTokenExpired();
      
      if (expired) {
        return await currentUser.getIdToken(true);
      } else {
        return await currentUser.getIdToken(false);
      }
    } catch (error) {
      return null;
    }
  };

  const fetchAuth = async (resource, init) => {
    try {
      if (!currentUser) {
        if (typeof window !== "undefined") {
          await navigate("/welcome/", { replace: true });
        }
        return null;
      }

      const _init = init || {};
      const headers = _init.headers || {};
      if (!headers["Content-type"])
        headers["Content-type"] = "application/json";
      
      const token = await refreshTokenIfNeeded();
      if (!token) {
        if (typeof window !== "undefined") {
          await navigate("/welcome/", { replace: true });
        }
        return null;
      }
      
      headers["Authorization"] = "Bearer " + token;
      const noXpub = _init["no-x-pubid"];
      delete _init["no-x-pubid"];
      if (typeof window !== "undefined" && !noXpub) {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (storedUser && storedUser.UserId) {
          headers["x-pubid"] = storedUser.UserId;
        }
      }
      
      _init.headers = headers;
      const apiResource = endPoint + resource;
      const response = await fetch(apiResource, _init);

      if (response.status === 401) {
        try {
          await currentUser.getIdToken(true);
          const retryResponse = await fetch(apiResource, _init);
          
          if (retryResponse.status === 401) {
            if (typeof window !== "undefined") {
              await navigate("/welcome/", { replace: true });
            }
            return null;
          }
          
          if (!retryResponse.ok) {
            const text = await retryResponse.text();
            
            return { ok: false, message: text };
          }
          
          if (headers["Content-type"] === "application/json") {
            const text = await retryResponse.text();
            try {
              return JSON.parse(text);
            } catch (e) {
              return { ok: false, message: "Invalid JSON response" };
            }
          }
          return await retryResponse.text();
        } catch (refreshError) {
          if (typeof window !== "undefined") {
            await navigate("/welcome/", { replace: true });
          }
          return null;
        }
      }

      if (!response.ok) {
        const text = await response.text();
        return { ok: false, message: text };
      }

      if (headers["Content-type"] === "application/json") {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          return { ok: false, message: "Invalid JSON response" };
        }
      }
      return await response.text();
    } catch (error) {
      if (typeof window !== "undefined") {
        await navigate("/welcome/", { replace: true });
      }
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthInitialized(true);
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthInitialized && !currentUser && typeof window !== "undefined" && 
        window.location.pathname !== "/welcome/" && 
        !window.location.pathname.startsWith("/welcome") &&
        window.location.pathname !== "/") {
      navigate("/welcome/", { replace: true });
    }
  }, [isAuthInitialized, currentUser]);

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      setCurrentUser, 
      fetchAuth,
      isAuthInitialized
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthValue() {
  return useContext(AuthContext);
}

export default AuthProvider;
